import { AppointmentTimelinePopup } from '@/components/AppointmentTimeline/Timeline';
import { LongTouchAction } from '@/components/LongTouchAction';
import PosButton from '@/components/PosButton';
import { RelativeTime } from '@/components/Time/RelativeTime';
import UserIntro from '@/components/User/UserIntro';
import { Appointment, AppointmentChannel, AppointmentStatus } from '@/models/appointment';
import { isServicePreOrder, PreOrder } from '@/models/order';
import { getIsPaidOrder } from '@/models/preOrder';
import { ID } from '@/root/global';
import { PullRequestOutlined, RollbackOutlined } from '@ant-design/icons';
import { Divider, Flex, Space } from 'antd';
import cx from 'classnames';
import { Fragment } from 'react/jsx-runtime';
import { ServiceTimer, useServiceTimerInfo } from './ServiceTimer';
import styles from './item.less';
import { UserInfo } from '@/components/User/UserInfo';

export const StationItem = ({
  preOrder,
  readonly,
  onRequestDelete,
  onRequestPop,
  onRequestChangeOperator,
  onRequestSetConfirmInfo,
  no,
}: {
  preOrder: PreOrder;
  no: ID;
  readonly: boolean;
  onRequestDelete: (preOrder: PreOrder) => void;
  onRequestPop: (preOrder: PreOrder) => void;
  onRequestChangeOperator: (preOrder: PreOrder) => void;
  onRequestSetConfirmInfo: ({ preOrder, appointment }: { preOrder: PreOrder; appointment?: Appointment }) => void;
}) => {
  const isPaidOrder = getIsPaidOrder(preOrder);
  const user = preOrder.userFullInfo?.user;
  const isService = isServicePreOrder(preOrder);
  const timerInfo = useServiceTimerInfo(preOrder, isService);

  return (
    <LongTouchAction
      className={cx(styles.stationItem, `${styles[`status-${timerInfo.status}`]}`)}
      actions={
        !readonly && (
          <>
            <PosButton
              danger
              type="primary"
              shape="round"
              onClick={e => {
                e.stopPropagation();
                onRequestDelete(preOrder);
              }}>
              <RollbackOutlined /> 撤单
            </PosButton>
          </>
        )
      }>
      <>
        <span className={styles.stationItemNo}>{no}</span>
        <div className={styles.stationItemHeader}>
          {isService ? (
            <ServiceTimer {...timerInfo} />
          ) : (
            <Flex justify="flex-end">
              <RelativeTime value={preOrder.addToStationTime} />
            </Flex>
          )}
        </div>

        <div className={styles.stationItemCenter}>
          {user ? (
            <div className="mb-s">
              <UserInfo user={user}>
                {({ Field }) => (
                  <Field type="modalTriggerWrapper">
                    <Flex gap={5}>
                      <Field type="avatar" avatarProps={{ size: 22 }} />
                      <Flex gap={0}>
                        <Field type="name" />
                      </Flex>
                    </Flex>
                  </Field>
                )}
              </UserInfo>
            </div>
          ) : null}

          <div className={styles.stationItemSkus}>
            {preOrder.specifications.map((specification, index) => (
              <Fragment key={specification.id}>
                {index !== 0 && <Divider className="mb-0 mt-0" dashed />}
                <div>
                  <>{specification.goodsName}</>
                  <>{specification.name ?? ''}</>
                  <> X {specification.number}</>
                </div>
              </Fragment>
            ))}
          </div>

          <Space className={styles.stationItemActions} direction="horizontal">
            {isService ? (
              preOrder.startTime ? (
                <></>
              ) : (
                !readonly && (
                  <AppointmentTimelinePopup
                    selectMode
                    disabled={Boolean(preOrder.appointment)}
                    filter={appointment =>
                      appointment.channel !== AppointmentChannel.Placeholder &&
                      appointment.status === AppointmentStatus.CONFIRMED
                    }
                    onChange={appointment => {
                      if (appointment === null) {
                        // 未预约，创建占位
                        onRequestSetConfirmInfo({
                          preOrder,
                        });
                      } else {
                        // 已预约，流转状态
                        onRequestSetConfirmInfo({
                          appointment,
                          preOrder,
                        });
                      }
                    }}>
                    <PosButton
                      key={'appointment'}
                      color="cyan"
                      variant="outlined"
                      onClick={() => {
                        // 外部传入的时候， preOrder 已经携带了预约信息，此处无需选择
                        if (preOrder.appointment) {
                          onRequestSetConfirmInfo({
                            appointment: preOrder.appointment,
                            preOrder,
                          });
                        }
                      }}>
                      开始服务
                    </PosButton>
                  </AppointmentTimelinePopup>
                )
              )
            ) : null}

            {!readonly && (
              <PosButton
                key={'view'}
                color="pink"
                variant="outlined"
                onClick={() => {
                  onRequestPop(preOrder);
                }}>
                <PullRequestOutlined /> {isPaidOrder ? '查看' : '结账'}
              </PosButton>
            )}

            {isService ? (
              <PosButton
                key="operator"
                onClick={() => {
                  if (!readonly) {
                    onRequestChangeOperator(preOrder);
                  }
                }}>
                {preOrder.operator ? <>{preOrder.operator.nickname}</> : <>美容师</>}
              </PosButton>
            ) : null}
          </Space>
        </div>
      </>
    </LongTouchAction>
  );
};
